import imageCompression from 'browser-image-compression';
import {
    MAX_DIMENSION_OPTIONS,
    canPreserveExif,
    isSupportedImageFile as isBaseImageFile,
} from '../compress/compressImageEngine';
import { encodeCanvas, resolveOutputFormat } from '../shared/imageOutputFormats';
import { needsDecoding, toDisplayableBlob } from '../shared/heicDisplay';

/**
 * The size list and the EXIF rule belong to whichever tool offers them, and now
 * both do — so they are read from the one place rather than written twice and
 * left to drift.
 */
export { MAX_DIMENSION_OPTIONS, canPreserveExif } from '../compress/compressImageEngine';

/** The compress tool's defaults, so the two settings cards open alike. */
export const DEFAULT_CONVERT_SETTINGS = {
    quality: 75,
    maxDimension: 'original',
    preserveExif: false,
};

const INPUT_EXT = /\.(jpe?g|png|webp|gif|bmp|heic|heif|avif)$/i;

const FORMAT_META = {
    jpg: { mime: 'image/jpeg', ext: 'jpg', quality: true, alpha: false },
    png: { mime: 'image/png', ext: 'png', quality: false, alpha: true },
    webp: { mime: 'image/webp', ext: 'webp', quality: true, alpha: true },
    avif: { mime: 'image/avif', ext: 'avif', quality: true, alpha: true },
};

/**
 * What the picker offers, in the order the other image tools offer it:
 * “original” first, then the formats this tool can write.
 */
export const OUTPUT_FORMATS = ['original', ...Object.keys(FORMAT_META)];

export function isSupportedConvertImageFile(file) {
    return isBaseImageFile(file)
        || file.type === 'image/avif'
        || INPUT_EXT.test(file.name || '');
}

// HEIC, TIFF and SVG cannot be drawn as they are — the shared decoder turns
// each into pixels (and remembers the result, so a preview already decoded
// is not decoded again here).
async function prepareFile(file) {
    if (!needsDecoding(file)) return file;
    const blob = await toDisplayableBlob(file);
    if (blob === file) return file;
    const base = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.${blob.type === 'image/png' ? 'png' : 'jpg'}`, { type: blob.type });
}

async function loadImageElement(file) {
    const prepared = await prepareFile(file);
    const url = URL.createObjectURL(prepared);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

function outputName(file, format, actualExt) {
    const base = file.name.replace(/\.[^.]+$/, '');
    const ext = actualExt || FORMAT_META[format]?.ext || format;
    return `${base}.${ext}`;
}

function result(file, format, blob, ext) {
    return {
        name: outputName(file, format, ext),
        blob,
        originalSize: file.size,
        convertedSize: blob.size,
    };
}

/**
 * Convert an image file to another format in the browser.
 *
 * @param {File} file
 * @param {string} format an id from OUTPUT_FORMATS
 * @param {{ quality?: number, maxDimension?: string, preserveExif?: boolean }} options
 *   quality — 1…100; maxDimension — an id from MAX_DIMENSION_OPTIONS
 */
export async function convertImageFile(file, format, options = {}) {
    // “Original” is per file, not per run: each one is resolved against its own
    // source, and the registry maps what the browser cannot write (HEIC → JPG,
    // GIF and BMP → PNG) onto what it can.
    const target = resolveOutputFormat(format, file);
    const meta = FORMAT_META[target] || FORMAT_META.jpg;
    const quality = Math.min(100, Math.max(1, Number(options.quality) || DEFAULT_CONVERT_SETTINGS.quality));
    const maxDimension = MAX_DIMENSION_OPTIONS.find((opt) => opt.id === options.maxDimension)?.value ?? null;

    // Drawing a picture on a canvas drops every tag it carried, so the one case
    // where EXIF can survive — JPEG in, JPEG out — goes through the compressor
    // instead, which copies the segment across. `canPreserveExif` is what says
    // this run is that case.
    if (options.preserveExif && canPreserveExif(file, format)) {
        const blob = await imageCompression(await prepareFile(file), {
            // No size ceiling: the quality dial is what decides the file size.
            maxSizeMB: Number.POSITIVE_INFINITY,
            ...(maxDimension ? { maxWidthOrHeight: maxDimension } : {}),
            useWebWorker: true,
            initialQuality: quality / 100,
            preserveExif: true,
            fileType: 'image/jpeg',
        });
        return result(file, 'jpg', blob);
    }

    const img = await loadImageElement(file);
    // The cap is on the longest side, and only ever shrinks — a picture already
    // smaller than it is left at its own size.
    const scale = maxDimension
        ? Math.min(1, maxDimension / Math.max(img.naturalWidth, img.naturalHeight))
        : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d', { alpha: meta.alpha });
    ctx.imageSmoothingQuality = 'high';

    if (!meta.alpha) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Shared encoder: real AVIF (WebAssembly where the canvas cannot write it)
    // instead of the old silent WebP, and the name follows what was written.
    const { blob, ext } = await encodeCanvas(canvas, FORMAT_META[target] ? target : 'jpg', { quality: quality / 100 });
    return result(file, target, blob, ext);
}

export { formatFileSize } from '../compress/compressImageEngine';
